const TOOLTIP_ESTIMATED_WIDTH = 250;
/** Exported: a hoverable panel spans this gap with a hit-strip so the pointer
 *  can cross it — see .hover-panel--interactive in HoverPanel.css. */
export const TOOLTIP_GAP = 4;
/** How far that strip reaches over the anchor's own edge, past the gap it spans.
 *  A hit test landing exactly on an edge the anchor shares with its neighbour
 *  resolves to the neighbour — one dead pixel, enough to read as "the pointer
 *  left" and drop the panel on the way in. */
const HOVER_BRIDGE_OVERLAP = 2;
/** The two together, as the inline custom properties the strip is drawn from.
 *  Kept beside the gap so the strip cannot drift from it; HoverPanel is the only
 *  reader. */
export const HOVER_BRIDGE_VARS = Object.freeze({
    '--hover-bridge': `${TOOLTIP_GAP}px`,
    '--hover-bridge-overlap': `${HOVER_BRIDGE_OVERLAP}px`,
});
const DEFAULT_Y_OFFSET = 2;
const SIMPLE_TOOLTIP_ESTIMATED_HEIGHT = 26;
const SIMPLE_TOOLTIP_GAP = 8;

/**
 * Calculates tooltip position based on element's bounding rect and viewport dimensions.
 * Defaults to right + top-anchored (grows downward) if enough space; flips left/right
 * and bottom-anchored (grows upward) as needed.
 * 
 * @param {DOMRect} rect - The bounding rectangle of the anchor element
 * @param {Object} options - Configuration options
 * @param {number} options.yOffset - Vertical offset from element edge (default: 2)
 * @param {boolean} options.useRightEdge - If true, use rect.right for right positioning, else use rect.left + rect.width (default: false)
 * @param {boolean} options.stacked - Place the tooltip above the anchor (below if
 *   there is no room) instead of beside it. For anchors sitting in a row, where
 *   a tooltip beside one covers its neighbours.
 * @returns {Object} Position object with x, y, goUpwards, and goLeftwards properties
 */
export const calculateTooltipPosition = (rect, options = {}) => {
    const {
        yOffset = DEFAULT_Y_OFFSET,
        useRightEdge = false,
        estimatedWidth = TOOLTIP_ESTIMATED_WIDTH,
        estimatedHeight = 200, // Default estimated height for side tooltips
        stacked = false,
    } = options;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Above the anchor, left-aligned and clamped to the viewport; below it when
    // there is no room above. Nothing in a horizontal row is covered either way.
    if (stacked) {
        const fitsAbove = rect.top >= estimatedHeight + TOOLTIP_GAP;
        const maxX = viewportWidth - estimatedWidth - TOOLTIP_GAP;
        return {
            x: Math.max(TOOLTIP_GAP, Math.min(rect.left, maxX)),
            y: fitsAbove ? rect.top - TOOLTIP_GAP : rect.bottom + TOOLTIP_GAP,
            goUpwards: fitsAbove,
            goLeftwards: false,
        };
    }

    // Top-anchor grows down from rect.top; bottom-anchor grows up from rect.bottom.
    // Prefer top when the tooltip fits in the viewport below the card's top edge
    // (not below the card's bottom — tall cards would always flip otherwise).
    const roomDownFromTop = viewportHeight - rect.top;
    const roomUpFromBottom = rect.bottom;
    const goUpwards = roomDownFromTop < estimatedHeight && roomUpFromBottom > roomDownFromTop;

    const spaceOnRight = viewportWidth - rect.right;
    const goLeftwards = spaceOnRight < estimatedWidth + TOOLTIP_GAP;

    // Calculate x position
    const x = goLeftwards
        ? rect.left - TOOLTIP_GAP
        : (useRightEdge ? rect.right + TOOLTIP_GAP : rect.left + rect.width + TOOLTIP_GAP);

    // Calculate y position
    let y = goUpwards
        ? rect.bottom + yOffset
        : rect.top - yOffset;
        
    // Clamp y to viewport bounds if it's still overflowing
    if (goUpwards && y - estimatedHeight < 0) {
        y = estimatedHeight; // Stick to top edge
    } else if (!goUpwards && y + estimatedHeight > viewportHeight) {
        y = viewportHeight - estimatedHeight; // Stick to bottom edge
    }

    return {
        x,
        y,
        goUpwards,
        goLeftwards
    };
};

/**
 * Calculates position for simple centered tooltips (used for buttons and UI elements).
 * Defaults to above if enough space, adapts to below if not enough space above.
 * 
 * @param {DOMRect} rect - The bounding rectangle of the anchor element
 * @param {Object} options - Configuration options
 * @param {number} options.gap - Vertical gap between element and tooltip (default: 8)
 * @param {number} options.estimatedHeight - Estimated tooltip height for space calculation (default: 26)
 * @param {number} options.estimatedWidth - If set, clamps horizontal position to prevent overflow
 * @returns {Object} Position object with top, left, and goUpwards properties
 */
export const calculateSimpleTooltipPosition = (rect, options = {}) => {
    const {
        gap = SIMPLE_TOOLTIP_GAP,
        estimatedHeight = SIMPLE_TOOLTIP_ESTIMATED_HEIGHT,
        estimatedWidth
    } = options;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Check space above and below
    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;

    // Default to above, but go below if not enough space above AND there's more space below
    const goUpwards = spaceAbove >= estimatedHeight + gap || spaceAbove > spaceBelow;

    // Calculate position
    let top = goUpwards
        ? rect.top - gap
        : rect.bottom + gap;
        
    // Clamp top to viewport bounds if it's still overflowing
    if (goUpwards && top - estimatedHeight < 0) {
        top = estimatedHeight; // Stick to top edge
    } else if (!goUpwards && top + estimatedHeight > viewportHeight) {
        top = viewportHeight - estimatedHeight; // Stick to bottom edge
    }

    let left = rect.left + rect.width / 2;

    if (estimatedWidth != null && estimatedWidth > 0) {
        const halfW = estimatedWidth / 2;
        left = Math.max(halfW, Math.min(viewportWidth - halfW, left));
    }

    return {
        top,
        left,
        goUpwards
    };
};
